import { Injectable } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { LiveShopService } from './liveshop.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class PMetaStoreService {

  private pixelsState: 'pending' | 'ready' | 'empty' = 'pending';
  private eventQueue: Array<{ eventName: string; eventData?: any }> = [];

  constructor(private liveShopService: LiveShopService) {
    this.tryResolveFromCache();
    const analyticsUrl = (environment as any).analyticsUrl;
    if (analyticsUrl) {
      (window as any).__slicksellAnalyticsUrl = analyticsUrl;
    }
  }

  private tryResolveFromCache(): void {
    try {
      const cached = localStorage.getItem('ss_pixels');
      if (!cached) return;
      const parsed = JSON.parse(cached);
      const hasPixels = (parsed?.facebook?.length ?? 0) > 0 || (parsed?.google?.length ?? 0) > 0;
      if (hasPixels) {
        this.pixelsState = 'ready';
        console.log('[pixel] pixelsState → ready (from cache no construtor)');
      }
    } catch {}
  }

  markPixelsReady(hasPixels: boolean): void {
    console.log('[pixel] markPixelsReady chamado:', hasPixels, '| fila:', this.eventQueue.length);
    if (!hasPixels) {
      this.pixelsState = 'empty';
      this.eventQueue = [];
      return;
    }
    this.pixelsState = 'ready';
    const queued = [...this.eventQueue];
    this.eventQueue = [];
    queued.forEach(e => this.sendPixelEvent(e.eventName, e.eventData));
  }

  sendBackendEvent(params: {
    type: string;
    details?: string;
    id_cta?: number;
    url?: string;
    slicks: any[];
    activeIndex: number;
    sessionId: string;
    playerSessionId: string;
  }): Observable<any> {
    console.log('sendBackendEvent 0', params);

    const {
      type,
      details = '',
      id_cta = 0,
      url = '',
      slicks,
      activeIndex,
      sessionId,
      playerSessionId,
    } = params;

    const currentSlick = slicks?.[activeIndex];
    const assetUid = currentSlick?.content?.uid;

    if (!assetUid || !sessionId || !playerSessionId) {
      const missing = {
        assetUid: !assetUid,
        sessionId: !sessionId,
        playerSessionId: !playerSessionId,
      };
      console.warn('sendBackendEvent early-return (missing required fields)', {
        missing,
        type,
        details,
        id_cta,
        url,
        activeIndex,
        slicksLength: Array.isArray(slicks) ? slicks.length : null,
        assetUid,
        sessionId,
        playerSessionId,
        currentSlickHasContent: !!currentSlick?.content,
      });
      return EMPTY;
    }

    const payload = this.mapTypeToBackendPayload(type, details, id_cta, url);
    console.log('sendBackendEvent 1', {
      type,
      details,
      id_cta,
      url,
      activeIndex,
      assetUid,
      sessionId,
      playerSessionId,
      payload,
    });

    return this.liveShopService.createPlayerEvent(sessionId, playerSessionId, assetUid, payload);
  }

  private mapTypeToBackendPayload(
    type: string,
    details: string,
    id_cta: number,
    url: string
  ): { event: string; details?: any } {
    switch (type) {
      case 'play':
        return { event: 'play', details: { type: details } };
      case 'like':
        return { event: 'ss_like' };
      case 'cta_click':
      case 'cta_events':
        return { event: 'ss_cta_click', details: { type: details, title: 'Tree', id_cta } };
      case 'cta_redirect':
        return { event: 'ss_cta_redirect', details: { type: details, title: 'Tree', id_cta, url } };
      case 'share':
        return { event: 'ss_share' };
      case 'slideMove':
        return { event: 'ss_swipe' };
      case 'pip':
        return { event: 'ss_pip' };
      case 'mute':
        return { event: 'ss_mute' };
      case 'unmute':
        return { event: 'ss_unmute' };
      case 'openChat':
        return { event: 'ss_open_chat' };
      case 'maximize':
        return { event: 'ss_maximize' };
      case 'minimize':
        return { event: 'ss_minimize' };
      case 'session_id':
        return { event: 'ss_session_id', details: { session_id: details } };
      case 'print':
        return { event: 'ss_print', details: { session_id: details } };
      default:
        return { event: type, details: { details } };
    }
  }

  // Método para adicionar um produto ao carrinho
  addProductToCart(product: any, cart: any[], show: any, products: any[]) {
    const event = new CustomEvent('cartEvent', {
      detail: {
        action: 'add_to_cart',
        item: product,
        cart: cart,
        show: show,
        products: products
      }
    });
    window.dispatchEvent(event);
  }

  // Método para finalizar a compra
  checkoutCart(cart: any[]) {
    const event = new CustomEvent('cartEvent', {
      detail: {
        action: 'checkout_cart',
        cart: cart
      }
    });
    window.dispatchEvent(event);
  }

  // Simulação de disparo de eventos para teste
  testEvents() {
    this.addProductToCart({ id: 1, name: 'Produto 1' }, [], true, []);
    this.checkoutCart([{ id: 1, name: 'Produto 1' }]);
  }

  sendEvents(event: any) {
    window.dispatchEvent(event);
  }

  sendPixelEventPublic(eventName: string, eventData?: any): void {
    this.sendPixelEvent(eventName, eventData);
  }

  sendAddToCartPixelEvent(detail: any, assetUid?: string): void {
    const qty = Number(detail.qty || 1);
    const price = Number(detail.price || 0);
    this.sendPixelEvent('ss_add_to_cart', {
      asset_uid: assetUid ?? null,
      // campos legados (Meta worker)
      sku: detail.sku,
      title: detail.title,
      category: detail.category,
      qty,
      price,
      // campos GA4
      currency: 'BRL',
      value: price * qty,
      items: [{
        item_id: String(detail.sku || ''),
        item_name: String(detail.title || ''),
        price,
        quantity: qty,
        ...(detail.category ? { item_category: String(detail.category) } : {}),
      }],
    });
  }

  sendProductViewEvent(product: any, assetUid: string | null): void {
    const itemId = String(product?.sku ?? product?.id ?? '');
    const itemName = String(product?.title ?? product?.name ?? '');
    const price = Number(product?.price ?? 0);
    const category = product?.category ?? (Array.isArray(product?.categories) ? product.categories[0] : null);

    window.dispatchEvent(new CustomEvent('customEvents', {
      detail: {
        event: 'ss_product_view',
        sku: itemId,
        title: itemName,
        category,
        categories: product?.categories ?? [],
      }
    }));

    this.sendPixelEvent('ss_product_view', {
      asset_uid: assetUid,
      item_id: itemId,
      item_name: itemName,
      currency: 'BRL',
      value: price,
      items: [{
        item_id: itemId,
        item_name: itemName,
        price,
        quantity: 1,
        ...(category ? { item_category: String(category) } : {}),
      }],
    });
  }

  sendBeginCheckoutEvent(cartItems: any[], currency = 'BRL'): void {
    const items = this.buildGa4Items(cartItems);
    const value = items.reduce((sum, it) => sum + it.price * it.quantity, 0);
    this.sendPixelEvent('ss_begin_checkout', { currency, value, items });
  }

  sendRemoveFromCartEvent(item: any, currency = 'BRL'): void {
    console.log('[pixel] sendRemoveFromCartEvent chamado — item recebido:', item);
    const qty = Number(item.quantity || 1);
    const price = Number(item.price || 0);
    const itemId = String(item.variant_id || item.id || '');
    const itemName = String(item.name || '');
    const detail = {
      event: 'ss_remove_from_cart',
      sku: itemId,
      title: itemName,
      price,
      qty,
      currency,
      value: price * qty,
    };
    console.log('[pixel] sendRemoveFromCartEvent — dispatchEvent customEvents:', detail);
    window.dispatchEvent(new CustomEvent('customEvents', { detail }));
    this.sendPixelEvent('ss_remove_from_cart', {
      currency,
      value: price * qty,
      items: [{
        item_id: itemId,
        item_name: itemName,
        price,
        quantity: qty,
      }],
    });
    console.log('[pixel] sendRemoveFromCartEvent — sendPixelEvent disparado');
  }

  sendViewCartEvent(cartItems: any[], currency = 'BRL'): void {
    console.log('[pixel] sendViewCartEvent chamado 2.2 — cartItems:', cartItems);
    if (!cartItems?.length) {
      console.log('[pixel] sendViewCartEvent — ignorado: carrinho vazio');
      return;
    }
    const items = this.buildGa4Items(cartItems);
    const value = items.reduce((sum, it) => sum + it.price * it.quantity, 0);
    const detail = { event: 'ss_view_cart', currency, value, items };
    console.log('[pixel] sendViewCartEvent 3— dispatchEvent customEvents:', detail);
    window.dispatchEvent(new CustomEvent('customEvents', { detail }));
    this.sendPixelEvent('ss_view_cart', { currency, value, items });
    console.log('[pixel] sendViewCartEvent 4— sendPixelEvent disparado');
  }

  private buildGa4Items(cartItems: any[]): { item_id: string; item_name: string; price: number; quantity: number; item_category?: string }[] {
    return cartItems.map(item => {
      const ga4Item: any = {
        item_id: String(item.variant_id || item.id || ''),
        item_name: String(item.name || ''),
        price: Number(item.price || 0),
        quantity: Number(item.quantity || 1),
      };
      const cats = Array.isArray(item.categories) ? item.categories : [];
      const cat = item.category || cats[0];
      if (cat) ga4Item.item_category = String(cat);
      return ga4Item;
    });
  }

  private isRunningInsideShopifyStorefront(): boolean {
    // heurística simples: Shopify costuma expor window.Shopify em storefront
    return typeof (window as any).Shopify !== 'undefined';
  }
  private isRunningInsideNuvemshopStorefront(): boolean {
    const LS = (window as any).LS;
    return !!(LS?.store);
  }

  private isOnSupportedStorefront(): boolean {
    return this.isRunningInsideShopifyStorefront() || this.isRunningInsideNuvemshopStorefront();
  }

  private getPixelWorkerUrl(): string {
    // chave existe apenas nos environments do build-widget (environment.widget.*)
    return (environment as any).pixelWorkerUrl ?? 'https://cdn.slicksell.com.br/oculus.js';
  }

  private getFbpCookie(): string | null {
    const match = document.cookie.split('; ').find(c => c.startsWith('_fbp='));
    return match ? match.split('=')[1] : null;
  }

  private sendPixelEvent(eventName: string, eventData?: any): void {
    console.log('[pixel] sendPixelEvent chamado:', eventName, '| state:', this.pixelsState);
    if (!this.isOnSupportedStorefront()) {
      console.log('[pixel] sendPixelEvent IGNORADO: não é storefront suportado', eventName);
      return;
    }
    console.log('sendPixelEvent 2')
    if (this.pixelsState === 'empty') {
      console.log('[pixel] sendPixelEvent IGNORADO: pixelsState=empty', eventName);
      return;
    }
    console.log('sendPixelEvent 3')
    if (this.pixelsState === 'pending') {
      console.log('[pixel] sendPixelEvent ENFILEIRADO (pending):', eventName);
      this.eventQueue.push({ eventName, eventData });
      return;
    }
    console.log('sendPixelEvent 4')
    const pixelsJson = localStorage.getItem('ss_pixels');
    if (!pixelsJson) {
      console.log('[pixel] sendPixelEvent IGNORADO: sem localStorage pixels', eventName);
      return;
    }
    console.log('sendPixelEvent 5')
    let pixels: { facebook: any[]; google: any[] };
    try {
      pixels = JSON.parse(pixelsJson);
    } catch {
      console.log('sendPixelEvent 6')
      return;
    }
    console.log('[pixel] sendPixelEvent executando:', eventName, { google: pixels.google?.length, __ssGA4: !!(window as any).__ssGA4 });

    const { asset_uid, ...data } = eventData ?? {};
    const fbp = this.getFbpCookie();

    // Meta — respeita controle por evento (true/false por pixel)
    pixels.facebook?.forEach(pixel => {
      const viewEnabled = pixel.events?.view?.[eventName];
      const conversionEvent = pixel.events?.conversion?.[eventName];
      const conversionEnabled = conversionEvent?.enabled;

      if (!viewEnabled && !conversionEnabled) return;

      const payload = {
        pixel_id: pixel.id,
        category: pixel.category,
        event: eventName,
        access_token: pixel.access_token ?? null,
        asset_uid: asset_uid ?? null,
        fbp: fbp ?? null,
        data
      };

      fetch(this.getPixelWorkerUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).catch(() => null);
    });

    // GA4 — delega ao script injetado window.__ssGA4 (ga4-tracker.js)
    const ssGA4 = (window as any).__ssGA4;
    if (!ssGA4?.track) {
      console.log('[pixel] GA4 ignorado: window.__ssGA4 não disponível', eventName);
    } else {
      const measurementIds = (pixels.google ?? [])
        .map((p: any) => p.measurementId)
        .filter(Boolean);

      console.log('[pixel] GA4 measurementIds:', measurementIds, '— pixels.google:', pixels.google);

      if (measurementIds.length > 0) {
        const storeId = localStorage.getItem('ss_SelectedStoreId') ?? null;
        const baseUrl = (environment as any).analyticsBaseUrl ?? null;
        const analyticsUrl = baseUrl ? `${baseUrl}/api/events/collect` : null;
        ssGA4.track(eventName, {
          measurementIds,
          slicksell_version: true,
          asset_uid: asset_uid ?? null,
          store_id: storeId ? Number(storeId) : null,
          analyticsUrl,
          ...data,
        });
        console.log('[pixel] ga4 enviado:', eventName, { measurementIds });
      } else {
        console.log('[pixel] GA4 ignorado: nenhum measurementId disponível em pixels.google', eventName);
      }
    }
  }

  // Novo método para enviar evento de progresso do player
  sendPlayerProgressEvent(
    currentProgress: number,
    watchedTime: number,
    buffered: number,
    pauses: number,
    forwards: number,
    rewards: number,
    assetUid: string,
    pitchTime: string,
    sessionId: string,
    playerSessionId: string
  ): Observable<any> {
    let event = new CustomEvent('playerEvents', {
      detail: {
        event: 'progress', // ou ss_view_percentage
        progress: currentProgress.toFixed(2),
        watchedTime: watchedTime,
        buffered: buffered,
        pauses: pauses,
        forwards: forwards,
        rewards: rewards,
        asset_uid: assetUid,
        pitch_time: pitchTime
      }
    });

    // Enviar evento
    this.sendEvents(event);
    this.sendPixelEvent('ss_view_percentage', { asset_uid: assetUid, progress: currentProgress.toFixed(2) });

    if (!assetUid || !sessionId || !playerSessionId) {
      return EMPTY;
    }
    console.log('sendPlayerProgressEvent')
    const seeks = (Number(forwards) || 0) + (Number(rewards) || 0);
    return this.liveShopService.createPlayerEvent(sessionId, playerSessionId, assetUid, {
      event: 'progress',
      details: {
        progress: currentProgress.toFixed(2),
        watched_time: watchedTime,
        buffered_time: buffered,
        pauses: pauses,
        seeks: seeks,
        pitch_time: pitchTime,
      }
    });
  }

  sendWidgetEvent(
    uid: string | undefined,
    type: string | undefined
  ) {
    let event = new CustomEvent('widgetEvents', {
      detail: {
        event: 'print',
        uid: uid,
        type: type
      }
    });

    // Enviar evento
    this.sendEvents(event);
  }
  // Novo método: Enviar evento de primeiro play
  sendFirstPlayEvent(activeIndex: number, slicks: any[], playerSessionId: string, type: string) {
    console.log('evento play')
    const currentSlick = slicks[activeIndex];

    if (currentSlick && currentSlick.content) {
      const currentUid = currentSlick.content.uid;

      let event = new CustomEvent('playerEvents', {
        detail: {
          event: 'play',
          asset_uid: currentUid,
          playerSessionId: playerSessionId,
          type: type,
        }
      });

      this.sendEvents(event);
      this.sendPixelEvent('ss_first_play', { asset_uid: currentUid, type });
      sessionStorage.setItem('ss_is_ss_impacted', 'true');
    }
  }

  // Novo método: Enviar evento de pitch time
  sendPitchTimeEvent(activeIndex: number, slicks: any[]) {
    const currentSlick = slicks[activeIndex];

    if (currentSlick && currentSlick.content) {
      const currentUid = currentSlick.content.uid;

      // Recuperar os dados do player do localStorage
      const playerDataJSON = localStorage.getItem('ss_player_data');
      if (playerDataJSON) {
        const playerData = JSON.parse(playerDataJSON);
        const pitchTime = playerData.current_asset.pitch_time;

        let event = new CustomEvent('playerEvents', {
          detail: {
            event: 'pitch',
            asset_uid: currentUid,
            pitch_time: pitchTime // Usar o pitch_time recuperado
          }
        });

        this.sendEvents(event);
        this.sendPixelEvent('ss_pitch_hit', { asset_uid: currentUid, pitch_time: pitchTime });
      }
    }
  }
  // Método para enviar eventos customizados com base no tipo
  sendCustomEvents(
    type: string,
    details: string,
    id_cta: number,
    slicks: any[],
    activeIndex: number,
    tempUrl: string
  ): void {

    const currentSlick = slicks[activeIndex];
    if (!currentSlick || !currentSlick.content) {
      return;
    }
    const currentUid = currentSlick.content.uid;
    let event: CustomEvent;

    switch (type) {
      case 'like':
        event = new CustomEvent('customEvents', { detail: { event: 'ss_like', asset_uid: currentUid } });
        break;
      case 'cta_click':
        event = new CustomEvent('customEvents', { detail: { event: 'ss_cta_click', type: details, title: 'Tree', id_cta: id_cta, asset_uid: currentUid } });
        break;
      case 'cta_events':
        event = new CustomEvent('customEvents', { detail: { event: 'ss_cta_click', type: details, title: 'Tree', id_cta: id_cta, asset_uid: currentUid } });
        break;
      case 'cta_redirect':
        event = new CustomEvent('customEvents', { detail: { event: 'ss_cta_redirect', type: details, title: 'Tree', id_cta: id_cta, url: tempUrl, asset_uid: currentUid } });
        break;
      case 'share':
        event = new CustomEvent('customEvents', { detail: { event: 'ss_share', asset_uid: currentUid } });
        break;
      case 'slideMove':
        event = new CustomEvent('customEvents', { detail: { event: 'ss_swipe', asset_uid: currentUid } }); // x
        break;
      case 'pip':
        event = new CustomEvent('customEvents', { detail: { event: 'ss_pip', asset_uid: currentUid } }); 
        break;
      case 'mute':
        event = new CustomEvent('customEvents', { detail: { event: 'ss_mute', asset_uid: currentUid } });
        break;
      case 'unmute':
        event = new CustomEvent('customEvents', { detail: { event: 'ss_unmute', asset_uid: currentUid } });
        break;
      case 'openChat':
        event = new CustomEvent('customEvents', { detail: { event: 'ss_open_chat', asset_uid: currentUid } });
        break;
      case 'maximize':
        event = new CustomEvent('customEvents', { detail: { event: 'ss_maximize', asset_uid: currentUid } });
        break;
      case 'minimize':
        event = new CustomEvent('customEvents', { detail: { event: 'ss_minimize', asset_uid: currentUid } });
        break;
      case 'session_id':
        event = new CustomEvent('customEvents', { detail: { event: 'ss_session_id', session_id: details } });
        break;
      case 'print':
        event = new CustomEvent('customEvents', { detail: { event: 'ss_print', session_id: details } });
        break;
      default:
        event = new CustomEvent(type, { detail: { details } });
    }

    this.sendEvents(event);
    this.sendPixelEvent((event as CustomEvent).detail?.event, { asset_uid: currentUid });
  }
}
